/**
 * Client Care Cost Analysis — Staff Labour Cost per Service User.
 *
 * Manager/Admin only screen (backend `require_role("admin","manager")` on
 * `/api/payroll/cost-per-client`). Care workers get a clear stub instead
 * of hitting a 403 spinner.
 *
 * Reuses the backend payload verbatim — no client-side recomputation of
 * historical cost. Renders:
 *   • 5 summary cards (approved hours, labour cost, cost/hr, unattributed
 *     hours, unattributed labour cost).
 *   • Unattributed warning card with reason breakdown.
 *   • Sortable per-client table.
 *   • Click-through drawer showing the underlying approved timesheet
 *     lines behind each client's rollup.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  BadgePoundSterling, Clock, Timer, AlertTriangle, ChevronRight,
  Search, RefreshCcw, Info, FileText, Download, TrendingUp, Settings, Zap,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

// ---- utils ---------------------------------------------------------------
// Timezone-safe date helpers — do NOT reintroduce `.toISOString().slice(0, 10)`
// here. See lib/dateWeek.js and lib/__tests__/dateWeek.test.js.
import { mondayOf, todayISO, addWeeks } from "@/lib/dateWeek";
const money = (v, ccy = "GBP") =>
  v == null ? "—" : new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy }).format(v);
const hrs = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}h`);

const REASON_LABEL = {
  visit_missing:         "Visit deleted",
  cross_tenant:          "Cross-tenant visit id",
  no_client_on_visit:    "Visit missing service user",
  line_missing_visit_id: "Line missing visit link",
};

// ---- summary cards -------------------------------------------------------
function SummaryCard({ icon: Icon, label, value, sub, tone = "info", testid }) {
  const tones = {
    info: "border-sky-200 bg-sky-50 text-sky-900",
    cost: "border-indigo-200 bg-indigo-50 text-indigo-900",
    ok:   "border-emerald-200 bg-emerald-50 text-emerald-900",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
  };
  return (
    <div className={`border rounded-lg p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-80 font-semibold flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-heading font-semibold mt-1 tabular-nums" data-testid={`${testid}-value`}>
        {value}
      </div>
      {sub ? <div className="text-[11px] opacity-70 mt-0.5">{sub}</div> : null}
    </div>
  );
}

// ---- main ---------------------------------------------------------------
export default function ClientCareCost() {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const isManager = ["admin", "manager", "registered_manager",
    "deputy_manager", "service_manager", "super_admin"].includes(role);

  const [weekStart, setWeekStart] = useState(() => mondayOf(todayISO()));
  const [siteId, setSiteId] = useState("all");
  const [clientId, setClientId] = useState("all");
  const [sites, setSites] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drawerClient, setDrawerClient] = useState(null);
  const [drawerLines, setDrawerLines] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [staffMap, setStaffMap] = useState({});
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const [trendWeeks, setTrendWeeks] = useState(8);
  const [trend, setTrend] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  // Overtime attention state — Admin can PUT settings; Manager view-only.
  const isAdmin = ["admin", "super_admin"].includes(role);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [threshold, setThreshold] = useState(20);
  const [thresholdSaving, setThresholdSaving] = useState(false);
  const [thresholdDraft, setThresholdDraft] = useState(20);
  // OT root-cause drawer state
  const [otDrawer, setOtDrawer] = useState(null);  // holds {client_id, client_name}
  const [otDetail, setOtDetail] = useState(null);
  const [otLoading, setOtLoading] = useState(false);

  const onExport = useCallback(async () => {
    if (!isManager || !data || data.empty) return;
    setExporting(true);
    try {
      const q = new URLSearchParams({ start_date: weekStart });
      if (siteId !== "all") q.set("site_id", siteId);
      if (clientId !== "all") q.set("client_id", clientId);
      const r = await api.get(`/payroll/cost-per-client.csv?${q.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `client-care-cost_${data.period?.start_date}_${data.period?.end_date}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Cost export downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  }, [isManager, data, weekStart, siteId, clientId]);

  // Sites for the filter — admin sees all, manager sees their site only
  useEffect(() => {
    if (!isManager) return;
    api.get("/sites").then((r) => setSites(r.data || []))
      .catch(() => setSites([]));
    // Load current org threshold once. Manager gets read-only view of the same value.
    api.get("/payroll/org-settings")
      .then((r) => {
        const v = Number(r.data?.overtime_alert_threshold_pct ?? 20);
        setThreshold(v);
        setThresholdDraft(v);
      })
      .catch(() => {});
  }, [isManager]);

  // Derived: over-threshold counts from the current data payload.
  const overtime = data?.overtime || {};
  const aboveCount = overtime.above_threshold_count || 0;
  const approachingCount = overtime.approaching_count || 0;

  const load = useCallback(async () => {
    if (!isManager) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ start_date: weekStart });
      if (siteId !== "all") q.set("site_id", siteId);
      if (clientId !== "all") q.set("client_id", clientId);
      const r = await api.get(`/payroll/cost-per-client?${q.toString()}`);
      setData(r.data);
    } catch (e) {
      // 404 = no approved period for that week — treat as empty state, not toast noise.
      if (e?.response?.status === 404) {
        setData({ empty: true, message: e.response.data?.detail || "No approved payroll period for this week." });
      } else {
        setData(null);
        toast.error(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  }, [isManager, weekStart, siteId, clientId]);

  useEffect(() => { load(); }, [load]);

  const saveThreshold = useCallback(async () => {
    if (!isAdmin) return;
    const v = Number(thresholdDraft);
    if (!Number.isFinite(v) || v < 0 || v > 100) {
      toast.error("Threshold must be between 0 and 100");
      return;
    }
    setThresholdSaving(true);
    try {
      const r = await api.put("/payroll/org-settings", {
        data: { overtime_alert_threshold_pct: v },
      });
      const saved = Number(r.data?.overtime_alert_threshold_pct ?? v);
      setThreshold(saved);
      setThresholdDraft(saved);
      setSettingsOpen(false);
      toast.success(`Overtime threshold set to ${saved}%`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setThresholdSaving(false);
    }
  }, [isAdmin, thresholdDraft, load]);

  const openOtDrawer = useCallback(async (client) => {
    if (!data?.period?.id) return;
    setOtDrawer({ client_id: client.client_id, client_name: client.client_name, overtime_status: client.overtime_status });
    setOtDetail(null);
    setOtLoading(true);
    try {
      const r = await api.get(
        `/payroll/overtime-alert/client/${client.client_id}?period_id=${data.period.id}`,
      );
      setOtDetail(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setOtDrawer(null);
    } finally {
      setOtLoading(false);
    }
  }, [data]);

  // ---- Cost trend fetch (client-filter and site-filter aware) ------------
  const loadTrend = useCallback(async () => {
    if (!isManager) return;
    setTrendLoading(true);
    try {
      const q = new URLSearchParams({ weeks: String(trendWeeks) });
      if (siteId !== "all") q.set("site_id", siteId);
      if (clientId !== "all") q.set("client_id", clientId);
      const r = await api.get(`/payroll/cost-per-client/trend?${q.toString()}`);
      setTrend(r.data);
    } catch (e) {
      setTrend(null);
    } finally {
      setTrendLoading(false);
    }
  }, [isManager, trendWeeks, siteId, clientId]);
  useEffect(() => { loadTrend(); }, [loadTrend]);

  const chartData = useMemo(() => {
    return (trend?.series || []).map((p) => ({
      ...p,
      label: p.week_start ? p.week_start.slice(5) : "",
    }));
  }, [trend]);

  const openClient = useCallback(async (client) => {
    if (!data?.period?.id) return;
    setDrawerClient(client);
    setDrawerLoading(true);
    try {
      const [pr, staffR] = await Promise.all([
        api.get(`/payroll/periods/${data.period.id}`),
        api.get("/staff").catch(() => ({ data: [] })),
      ]);
      const allLines = (pr.data?.lines || []);
      // Frontend join = same read-time deref the backend uses. Requires
      // client_id (new tag) OR visit_id → visit.client_id. Backend already
      // did the classification when computing the rollup, but for the
      // detail drawer we display only the confirmed-attributed lines.
      const mine = allLines.filter((ln) => ln.client_id === client.client_id);
      setDrawerLines(mine);
      const smap = {};
      (staffR.data || []).forEach((s) => { smap[s.id] = s.name || s.email; });
      setStaffMap(smap);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDrawerLoading(false);
    }
  }, [data]);

  const filteredClients = useMemo(() => {
    const list = data?.clients || [];
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter((c) => (c.client_name || "").toLowerCase().includes(q));
  }, [data, search]);

  const unattr = data?.unattributed || {};
  const totals = data?.totals || {};
  const currency = data?.currency || "GBP";
  const period = data?.period;

  if (!isManager) {
    return (
      <div className="p-6" data-testid="cost-page-blocked">
        <div className="max-w-lg mx-auto border border-slate-200 bg-white rounded-lg p-6 mt-10 text-center">
          <div className="text-slate-400 mb-2 flex justify-center"><Info className="w-6 h-6" /></div>
          <div className="font-semibold text-slate-900">Manager / Admin access only</div>
          <div className="text-sm text-slate-600 mt-1">
            Client Care Cost Analysis is limited to managers and admins because it shows staff labour cost data.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="client-care-cost-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            Payroll · Analytics
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900 mt-1">
            Client Care Cost Analysis
          </h1>
          <div className="mt-2 inline-flex items-center gap-2 text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded-md px-2.5 py-1"
               data-testid="cost-page-scope-notice">
            <Info className="w-3.5 h-3.5" />
            {data?.notice || "Staff labour cost only — not full business profitability."}
          </div>
        </div>
        <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={load} disabled={loading}
                data-testid="cost-page-refresh">
          <RefreshCcw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        <Button variant="default" size="sm" onClick={onExport}
          disabled={exporting || !data || data.empty}
          data-testid="cost-export-csv">
          <Download className={`w-3.5 h-3.5 mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
          Export CSV
        </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-slate-200 rounded-lg bg-white p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
           data-testid="cost-filters">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">Payroll week</Label>
          <div className="mt-1 flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setWeekStart(addWeeks(weekStart, -1))}
                    data-testid="cost-week-prev">‹</Button>
            <Input type="date" value={weekStart}
                   onChange={(e) => setWeekStart(mondayOf(e.target.value))}
                   className="flex-1"
                   data-testid="cost-week-input" />
            <Button variant="outline" size="sm" onClick={() => setWeekStart(addWeeks(weekStart, +1))}
                    data-testid="cost-week-next">›</Button>
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            Snapped to the Monday of that week.
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">Site</Label>
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="mt-1" data-testid="cost-site-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites</SelectItem>
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">Service user</Label>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="mt-1" data-testid="cost-client-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All service users</SelectItem>
              {(data?.clients || []).map((c) => (
                <SelectItem key={c.client_id} value={c.client_id}>{c.client_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">Search</Label>
          <div className="mt-1 relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Filter by name…" value={search}
                   onChange={(e) => setSearch(e.target.value)}
                   className="pl-8"
                   data-testid="cost-search-input" />
          </div>
        </div>
      </div>

      {/* Empty state */}
      {data?.empty ? (
        <div className="border border-slate-200 bg-white rounded-lg p-8 text-center"
             data-testid="cost-empty-state">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <div className="font-semibold text-slate-900">No approved payroll period yet</div>
          <div className="text-sm text-slate-600 mt-1">
            {data.message || "Approve a payroll period for this week to see the client cost breakdown."}
          </div>
        </div>
      ) : null}

      {/* Period banner */}
      {period ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border border-slate-200 rounded-lg bg-white px-4 py-2.5"
             data-testid="cost-period-banner">
          <div className="text-sm text-slate-700">
            Payroll period <b className="text-slate-900">{period.start_date}</b> → <b className="text-slate-900">{period.end_date}</b>
            <span className="ml-2 inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
              {period.status}
            </span>
          </div>
          <div className="text-xs text-slate-500">Currency: <b className="text-slate-800">{currency}</b></div>
        </div>
      ) : null}

      {/* Summary cards */}
      {data && !data.empty ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
             data-testid="cost-summary-cards">
          <SummaryCard
            icon={Clock} tone="info" testid="card-total-hours"
            label="Approved Care Hours"
            value={hrs(totals.payable_hours)}
            sub={`Regular ${hrs(totals.regular_hours)} · OT ${hrs(totals.overtime_hours)}`}
          />
          <SummaryCard
            icon={BadgePoundSterling} tone="cost" testid="card-labour-cost"
            label="Staff Labour Cost"
            value={money(totals.estimated_cost, currency)}
            sub="Based on approved rate snapshots"
          />
          <SummaryCard
            icon={Timer} tone="ok" testid="card-cost-per-hour"
            label="Avg Cost / Care Hour"
            value={money(totals.cost_per_hour, currency)}
            sub="Weighted across attributed lines"
          />
          <SummaryCard
            icon={Clock} tone="warn" testid="card-unattr-hours"
            label="Unattributed Hours"
            value={hrs(unattr.payable_hours)}
            sub={`${unattr.line_count || 0} line${(unattr.line_count || 0) === 1 ? "" : "s"}`}
          />
          <SummaryCard
            icon={BadgePoundSterling} tone="warn" testid="card-unattr-cost"
            label="Unattributed Labour Cost"
            value={money(unattr.estimated_cost, currency)}
            sub="Investigate below"
          />
          <SummaryCard
            icon={Zap} tone={aboveCount > 0 ? "warn" : "ok"} testid="card-overtime-attention"
            label="Overtime Attention"
            value={`${aboveCount}`}
            sub={`${approachingCount} approaching · threshold ${threshold}%`}
          />
        </div>
      ) : null}

      {/* Overtime settings row (Admin can edit threshold, Manager view-only) */}
      {data && !data.empty ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border border-slate-200 rounded-lg bg-white px-4 py-2.5"
             data-testid="cost-overtime-settings-row">
          <div className="text-sm text-slate-700 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-600" />
            Overtime alert threshold:
            <b className="text-slate-900 tabular-nums">{threshold}%</b>
            <span className="text-[11px] text-slate-500">
              of a client's approved care hours
            </span>
            {!overtime.editable_alert ? (
              <span className="ml-2 inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200">
                Historical view
              </span>
            ) : null}
          </div>
          {isAdmin ? (
            <Button size="sm" variant="outline" onClick={() => { setThresholdDraft(threshold); setSettingsOpen(true); }}
                    data-testid="cost-overtime-threshold-open">
              <Settings className="w-3.5 h-3.5 mr-1.5" /> Configure
            </Button>
          ) : (
            <span className="text-[11px] text-slate-500">Admin-only setting</span>
          )}
        </div>
      ) : null}

      {/* Unattributed warning card */}
      {data && !data.empty && (unattr.line_count || 0) > 0 ? (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4"
             data-testid="cost-unattributed-card">
          <div className="flex items-start gap-3">
            <div className="text-amber-800 mt-0.5"><AlertTriangle className="w-5 h-5" /></div>
            <div className="flex-1">
              <div className="font-semibold text-amber-900">
                {unattr.line_count} labour {unattr.line_count === 1 ? "record has" : "records have"} no service user attribution
              </div>
              <div className="text-sm text-amber-800 mt-0.5">
                {money(unattr.estimated_cost, currency)} of staff labour ({hrs(unattr.payable_hours)}) cannot be linked to a service user.
                We never guess an attribution by site — please review the underlying visits.
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="cost-unattributed-reasons">
                {Object.entries(unattr.reason_counts || {}).filter(([, n]) => (n || 0) > 0).map(([k, n]) => (
                  <span key={k}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-900"
                        data-testid={`cost-unattr-reason-${k}`}>
                    {REASON_LABEL[k] || k}: <b>{n}</b>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Cost Trend Chart */}
      {data && !data.empty ? (
        <div className="border border-slate-200 rounded-lg bg-white overflow-hidden"
             data-testid="cost-trend-chart-card">
          <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                <TrendingUp className="w-4 h-4 text-indigo-600" />
                Staff Labour Cost Trend
              </div>
              <div className="text-[11px] text-slate-500">
                Week-over-week using approved payroll snapshots — not profitability.
              </div>
            </div>
            <div className="flex items-center gap-1" role="group" data-testid="cost-trend-range">
              {[4, 8, 12].map((n) => (
                <Button key={n}
                        variant={trendWeeks === n ? "default" : "outline"}
                        size="sm"
                        onClick={() => setTrendWeeks(n)}
                        data-testid={`cost-trend-range-${n}`}>
                  {n}w
                </Button>
              ))}
            </div>
          </div>
          <div className="px-4 py-3">
            {trendLoading ? (
              <div className="h-56 flex items-center justify-center text-sm text-slate-400">Loading trend…</div>
            ) : (chartData.length < 2) ? (
              <div className="h-56 flex flex-col items-center justify-center text-center gap-1"
                   data-testid="cost-trend-empty">
                <FileText className="w-6 h-6 text-slate-300" />
                <div className="text-sm font-medium text-slate-700">Not enough historical data yet</div>
                <div className="text-[11px] text-slate-500 max-w-xs">
                  A trend needs at least two completed payroll weeks with the current filter.
                </div>
              </div>
            ) : (
              <div className="h-64 -ml-2" data-testid="cost-trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e5e7eb" }} />
                    <YAxis yAxisId="cost" orientation="left" tick={{ fontSize: 11, fill: "#4338ca" }}
                           tickLine={false} axisLine={{ stroke: "#e5e7eb" }}
                           tickFormatter={(v) => `£${Math.round(v)}`} />
                    <YAxis yAxisId="hours" orientation="right" tick={{ fontSize: 11, fill: "#0369a1" }}
                           tickLine={false} axisLine={{ stroke: "#e5e7eb" }}
                           tickFormatter={(v) => `${Math.round(v)}h`} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12, background: "#fff" }}
                      labelFormatter={(l, payload) => {
                        const pt = payload?.[0]?.payload;
                        return pt ? `${pt.week_start} → ${pt.week_end}` : l;
                      }}
                      formatter={(val, name, ctx) => {
                        const pt = ctx?.payload || {};
                        if (name === "Labour Cost") {
                          const pct = pt.cost_delta_pct;
                          const suffix = pct == null ? "" : `  (${pct > 0 ? "+" : ""}${pct}% vs prev)`;
                          return [`${money(val, currency)}${suffix}`, name];
                        }
                        if (name === "Care Hours") return [hrs(val), name];
                        if (name === "Cost / hr") return [money(val, currency), name];
                        return [val, name];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                    <Line yAxisId="cost"  type="monotone" dataKey="estimated_cost" stroke="#4338ca"
                          strokeWidth={2.5} name="Labour Cost" dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    <Line yAxisId="hours" type="monotone" dataKey="payable_hours"  stroke="#0369a1"
                          strokeWidth={2} name="Care Hours"  dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    <Line yAxisId="cost"  type="monotone" dataKey="cost_per_hour"  stroke="#059669"
                          strokeWidth={1.5} strokeDasharray="4 3" name="Cost / hr" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Per-client table */}
      {data && !data.empty ? (
        <div className="border border-slate-200 rounded-lg bg-white overflow-hidden"
             data-testid="cost-clients-table">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-900">Per service user</div>
              <div className="text-[11px] text-slate-500">Click a row to see the underlying approved visits.</div>
            </div>
            <div className="text-[11px] text-slate-500 tabular-nums">
              {filteredClients.length} of {data.clients.length} service users
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2 font-semibold">Service User</th>
                  <th className="px-4 py-2 font-semibold text-right">Care Hours</th>
                  <th className="px-4 py-2 font-semibold text-right">Regular</th>
                  <th className="px-4 py-2 font-semibold text-right">Overtime</th>
                  <th className="px-4 py-2 font-semibold text-right">OT %</th>
                  <th className="px-4 py-2 font-semibold text-right">Labour Cost</th>
                  <th className="px-4 py-2 font-semibold text-right">Cost / hr</th>
                  <th className="px-4 py-2 font-semibold text-right w-8"></th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500 text-sm">
                    No service users match the current filters.
                  </td></tr>
                ) : filteredClients.map((c) => {
                  const ot = c.overtime_status || "normal";
                  const otBadge =
                    ot === "above"       ? { bg: "bg-amber-100", fg: "text-amber-900", border: "border-amber-300", label: "Above Threshold" }
                  : ot === "approaching" ? { bg: "bg-amber-50",  fg: "text-amber-800", border: "border-amber-200", label: "Approaching" }
                  :                        { bg: "bg-slate-50",  fg: "text-slate-600", border: "border-slate-200", label: "Normal" };
                  return (
                  <tr key={c.client_id}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => openClient(c)}
                      data-testid={`cost-client-row-${c.client_id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-900">{c.client_name}</div>
                      <div className="text-[11px] text-slate-500">
                        {c.staff_count} staff · {c.line_count} visit{c.line_count === 1 ? "" : "s"}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{hrs(c.payable_hours)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{hrs(c.regular_hours)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {c.overtime_hours > 0 ? (
                        <span className="inline-flex items-center gap-1 text-amber-800 font-semibold">
                          {hrs(c.overtime_hours)}
                        </span>
                      ) : hrs(c.overtime_hours)}
                    </td>
                    <td className="px-4 py-2.5 text-right"
                        data-testid={`cost-client-ot-${c.client_id}`}>
                      <div className="inline-flex items-center gap-1.5 tabular-nums">
                        <span className="text-slate-700 font-semibold">{(c.overtime_pct ?? 0).toFixed(1)}%</span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${otBadge.bg} ${otBadge.fg} ${otBadge.border}`}
                              data-testid={`cost-client-ot-status-${c.client_id}`}>
                          {otBadge.label}
                        </span>
                        {ot !== "normal" ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openOtDrawer(c); }}
                            className="text-[10px] text-amber-800 hover:text-amber-900 underline decoration-dotted"
                            data-testid={`cost-client-ot-drill-${c.client_id}`}>
                            Why?
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-indigo-900">
                      {money(c.estimated_cost, currency)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">
                      {money(c.cost_per_hour, currency)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-slate-400">
                      <ChevronRight className="w-4 h-4 inline-block" />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              {filteredClients.length > 0 ? (
                <tfoot className="bg-slate-50 border-t border-slate-200">
                  <tr className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                    <td className="px-4 py-2">Totals (attributed)</td>
                    <td className="px-4 py-2 text-right tabular-nums">{hrs(totals.payable_hours)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{hrs(totals.regular_hours)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{hrs(totals.overtime_hours)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {totals.payable_hours > 0
                        ? `${((totals.overtime_hours / totals.payable_hours) * 100).toFixed(1)}%`
                        : "—"}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-indigo-900">{money(totals.estimated_cost, currency)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(totals.cost_per_hour, currency)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </div>
      ) : null}

      {/* Client detail drawer */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md" data-testid="cost-overtime-settings-dialog">
          <DialogHeader>
            <DialogTitle>Overtime alert threshold</DialogTitle>
            <DialogDescription>
              Set the client-level overtime share at which the dashboard flags a service user as needing attention. Default is 20%.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="threshold-input" className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
              Threshold (%)
            </Label>
            <Input id="threshold-input" type="number" min="0" max="100" step="1"
                   value={thresholdDraft}
                   onChange={(e) => setThresholdDraft(e.target.value)}
                   data-testid="cost-overtime-threshold-input" />
            <div className="text-[11px] text-slate-500">
              High overtime may indicate cover or rota pressure; it is not a judgement about staffing.
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setSettingsOpen(false)}
                    data-testid="cost-overtime-threshold-cancel">Cancel</Button>
            <Button onClick={saveThreshold} disabled={thresholdSaving || !isAdmin}
                    data-testid="cost-overtime-threshold-save">
              {thresholdSaving ? "Saving…" : "Save threshold"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!drawerClient} onOpenChange={(o) => !o && setDrawerClient(null)}>
        <DialogContent className="max-w-3xl" data-testid="cost-client-drawer">
          <DialogHeader>
            <DialogTitle>
              {drawerClient?.client_name || "Service user"}
            </DialogTitle>
            <DialogDescription>
              Approved visits used to calculate this service user's staff labour cost for {period?.start_date} → {period?.end_date}.
            </DialogDescription>
          </DialogHeader>
          {drawerClient ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 my-3">
                <div className="border border-slate-200 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Care Hours</div>
                  <div className="text-lg font-heading tabular-nums">{hrs(drawerClient.payable_hours)}</div>
                </div>
                <div className="border border-slate-200 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Overtime</div>
                  <div className="text-lg font-heading tabular-nums">{hrs(drawerClient.overtime_hours)}</div>
                </div>
                <div className="border border-slate-200 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Labour Cost</div>
                  <div className="text-lg font-heading tabular-nums text-indigo-900">{money(drawerClient.estimated_cost, currency)}</div>
                </div>
                <div className="border border-slate-200 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Cost / hr</div>
                  <div className="text-lg font-heading tabular-nums">{money(drawerClient.cost_per_hour, currency)}</div>
                </div>
              </div>
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
                  <span>Underlying approved lines</span>
                  <span className="text-slate-400 tabular-nums">{drawerLines.length} line{drawerLines.length === 1 ? "" : "s"}</span>
                </div>
                <div className="max-h-[50vh] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white border-b border-slate-100">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                        <th className="px-3 py-2 font-semibold">Date</th>
                        <th className="px-3 py-2 font-semibold">Staff</th>
                        <th className="px-3 py-2 font-semibold text-right">Payable</th>
                        <th className="px-3 py-2 font-semibold text-right">Overtime</th>
                        <th className="px-3 py-2 font-semibold text-right">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drawerLoading ? (
                        <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500 text-sm">Loading…</td></tr>
                      ) : drawerLines.length === 0 ? (
                        <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500 text-sm">No underlying lines available.</td></tr>
                      ) : drawerLines.map((ln) => (
                        <tr key={ln.id} className="border-t border-slate-100">
                          <td className="px-3 py-2 tabular-nums text-slate-700">{(ln.scheduled_start || "").slice(0, 10)}</td>
                          <td className="px-3 py-2 text-slate-900">{staffMap[ln.staff_user_id] || "—"}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{hrs(ln.payable_hours)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{hrs(ln.overtime_hours)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-indigo-900 font-semibold">
                            {money(ln.estimated_cost, currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="text-[11px] text-slate-500 mt-2">
                Cost figures use each line's stored rate snapshot from the moment the payroll period was approved — historical rates are never recomputed.
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* OT Root-Cause drawer — opens on "Why?" from Approaching/Above rows */}
      <Dialog open={!!otDrawer} onOpenChange={(o) => !o && setOtDrawer(null)}>
        <DialogContent className="max-w-3xl" data-testid="ot-root-cause-drawer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-600" />
              Overtime breakdown — {otDrawer?.client_name || "Service user"}
            </DialogTitle>
            <DialogDescription>
              {otDetail?.note || "High overtime may indicate cover or rota pressure; it is not a judgement about any staff member or rota."}
            </DialogDescription>
          </DialogHeader>
          {otLoading ? (
            <div className="py-10 text-center text-sm text-slate-400">Loading breakdown…</div>
          ) : otDetail ? (
            <>
              {/* Summary strip */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 my-3"
                   data-testid="ot-drawer-summary">
                <div className="border border-slate-200 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Care Hours</div>
                  <div className="text-lg font-heading tabular-nums">{hrs(otDetail.summary.payable_hours)}</div>
                </div>
                <div className="border border-amber-200 bg-amber-50 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Overtime</div>
                  <div className="text-lg font-heading tabular-nums text-amber-900">{hrs(otDetail.summary.overtime_hours)}</div>
                </div>
                <div className="border border-amber-200 bg-amber-50 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">OT %</div>
                  <div className="text-lg font-heading tabular-nums text-amber-900">{(otDetail.summary.overtime_pct ?? 0).toFixed(1)}%</div>
                </div>
                <div className="border border-slate-200 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Threshold</div>
                  <div className="text-lg font-heading tabular-nums">{otDetail.threshold_pct}%</div>
                </div>
                <div className="border border-slate-200 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Staff w/ OT</div>
                  <div className="text-lg font-heading tabular-nums">{otDetail.summary.staff_contributing_overtime}</div>
                </div>
              </div>

              {/* Contextual chips */}
              <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
                {otDetail.summary.highest_overtime_day ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-900"
                        data-testid="ot-drawer-highest-day">
                    Highest OT day: <b>{otDetail.summary.highest_overtime_day.date}</b> · {hrs(otDetail.summary.highest_overtime_day.overtime_hours)}
                  </span>
                ) : null}
                {otDetail.editable_alert ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-900">
                    Draft period — investigate before approval
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
                    Historical view · {otDetail.period.status}
                  </span>
                )}
              </div>

              {/* By staff */}
              <div className="border border-slate-200 rounded-md overflow-hidden mb-3" data-testid="ot-drawer-by-staff">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  By staff member — highest overtime first
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-white border-b border-slate-100">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="px-3 py-2 font-semibold">Staff</th>
                      <th className="px-3 py-2 font-semibold text-right">Shifts</th>
                      <th className="px-3 py-2 font-semibold text-right">Payable</th>
                      <th className="px-3 py-2 font-semibold text-right">Overtime</th>
                      <th className="px-3 py-2 font-semibold text-right">OT %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otDetail.by_staff.length === 0 ? (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500 text-sm">No overtime attributed.</td></tr>
                    ) : otDetail.by_staff.map((s) => (
                      <tr key={s.staff_user_id} className="border-t border-slate-100"
                          data-testid={`ot-drawer-staff-${s.staff_user_id}`}>
                        <td className="px-3 py-2 text-slate-900 font-medium">
                          {s.staff_name}
                          {s.primary_contributor ? (
                            <span className="ml-2 inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900"
                                  data-testid="ot-drawer-primary-contributor">
                              Primary overtime contributor
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{s.shifts}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{hrs(s.payable_hours)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-900 font-semibold">{hrs(s.overtime_hours)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{(s.overtime_pct ?? 0).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* By day */}
              <div className="border border-slate-200 rounded-md overflow-hidden mb-3" data-testid="ot-drawer-by-day">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  By day — highest overtime first
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-white border-b border-slate-100">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="px-3 py-2 font-semibold">Date</th>
                      <th className="px-3 py-2 font-semibold text-right">Shifts</th>
                      <th className="px-3 py-2 font-semibold text-right">Staff</th>
                      <th className="px-3 py-2 font-semibold text-right">Payable</th>
                      <th className="px-3 py-2 font-semibold text-right">Overtime</th>
                      <th className="px-3 py-2 font-semibold text-right">OT %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otDetail.by_day.map((d) => (
                      <tr key={d.date} className="border-t border-slate-100">
                        <td className="px-3 py-2 tabular-nums text-slate-700">{d.date}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{d.shifts}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{d.staff_count}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{hrs(d.payable_hours)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-900 font-semibold">{hrs(d.overtime_hours)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{(d.overtime_pct ?? 0).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Line-level detail */}
              <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="ot-drawer-lines">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
                  <span>Contributing shifts</span>
                  <span className="text-slate-400 tabular-nums">{otDetail.lines.length} shift{otDetail.lines.length === 1 ? "" : "s"}</span>
                </div>
                <div className="max-h-[35vh] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white border-b border-slate-100">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                        <th className="px-3 py-2 font-semibold">Date</th>
                        <th className="px-3 py-2 font-semibold">Staff</th>
                        <th className="px-3 py-2 font-semibold">Shift</th>
                        <th className="px-3 py-2 font-semibold">Site</th>
                        <th className="px-3 py-2 font-semibold text-right">Payable</th>
                        <th className="px-3 py-2 font-semibold text-right">OT</th>
                        <th className="px-3 py-2 font-semibold text-right">OT %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {otDetail.lines.map((ln) => (
                        <tr key={ln.id} className="border-t border-slate-100">
                          <td className="px-3 py-2 tabular-nums text-slate-700">{ln.date}</td>
                          <td className="px-3 py-2 text-slate-900">{ln.staff_name}</td>
                          <td className="px-3 py-2 tabular-nums text-slate-600">
                            {(ln.shift_start || "").slice(11, 16)}–{(ln.shift_end || "").slice(11, 16)}
                          </td>
                          <td className="px-3 py-2 text-slate-600">{ln.site_name || "—"}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{hrs(ln.payable_hours)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-amber-900 font-semibold">{hrs(ln.overtime_hours)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{(ln.overtime_pct ?? 0).toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
